/**
 * **A finished session, read on the card**: the sets done as the ring's big number, the four chips
 * that say what stood out (mood, sleep, sets skipped, records), the client's own words, the
 * exercises that went differently from the plan — and on the desk, the note field with its quick
 * replies, which is what resolves the item (attention-queue R15).
 *
 * Owner, 24 September 2026: the reflection and the deviations used to be behind „Zobacz sesję". They
 * come from the same `GET /workout-execution/{id}/display` read the chips already make, so they cost
 * nothing new; the read is still gated on the block being on screen.
 *
 * ── The note waits with the detail ──────────────────────────────────────────────────────────
 * The note field is open from the first frame and stands at the FOOT of the body (owner, 25 September
 * 2026: at the foot rather than under the detail, which left the band under it empty on a tall window). A detail that grew on arrival would push a field the
 * trainer may already be typing into, and a floor held for the tallest detail left an empty band
 * above the field for every shorter one. So the field is not drawn until the read has answered: the
 * reserve stands for the detail and the field together, and both arrive in one frame.
 *
 * ── The Pulpit's card reads left to right (owner, 26 September 2026) ────────────────────────
 * The ring and its caption on the left; beside it one column — the session's name and length, the
 * chips, the client's words — centred against the ring. Every part of that column holds its height
 * from the first frame (the chips a two-line band), so the read changes ink and never geometry.
 *
 * ── The Przegląd's card draws no ring (owner, 25 September 2026) ────────────────────────────
 * Its big figure went, and the width it took goes to the data: one line of chips (the capped
 * three, the counter among them, and the night's sleep), the three exercises that went differently
 * and the client's words on one line. The exercises say what the ring summarised.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOnScreen } from '~/shared/lib/useOnScreen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ReadoutRing } from '~/shared/ui/chart/ReadoutRing';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useHomeFormats } from '../lib/useHomeFormats';
import { useSessionReadout } from '../lib/useSessionReadout';
import type { TaskDensity } from '../lib/taskWrites';
import type { SessionFeedback } from '../model/feedbackShape';
import type { RingInk } from '../model/ringInk';
import { ExerciseLines } from './ExerciseLines';
import { FeedbackSignalChips } from './FeedbackSignalChips';
import styles from './taskBlocks.module.css';

const RING_COLOUR: Readonly<Record<RingInk, string>> = {
  good: 'var(--good)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
};

/**
 * The ring per density: the desk's 88 (104 until the note field joined the resting card, owner 25
 * September 2026) and the Pulpit's 56 (64 until the same day). The Przegląd's card draws none.
 */
const RING: Readonly<
  Record<Exclude<TaskDensity, 'quick'>, { readonly diameter: number; readonly thickness: number; readonly scale: 'key' | 'goal' | 'small' }>
> = {
  desk: { diameter: 88, thickness: 8, scale: 'key' },
  // The figure a size down in the small ring, so a three-digit count keeps air inside the stroke.
  short: { diameter: 56, thickness: 6, scale: 'small' },
};

interface SessionBlockProps {
  readonly session: SessionFeedback;
  readonly density: TaskDensity;
  /** The meta line the Pulpit's card moves from its head into the column beside the ring. */
  readonly glance: string;
  readonly reducedMotion: boolean;
  /** The note composer, the page's (`features/session-comment`). Desk only. */
  readonly note?: React.ReactNode;
}

export function SessionBlock({ session, density, glance, reducedMotion, note }: SessionBlockProps) {
  const { t } = useTranslation();
  const { decimal } = useHomeFormats();
  const boxRef = useRef<HTMLDivElement>(null);
  const { readout, chipsPending, detail, detailFailed } = useSessionReadout(session, useOnScreen(boxRef));
  const gate = useSkeletonGate(chipsPending);
  const sleep = detail?.sleepHours ?? null;

  if (density === 'quick') {
    return (
      <div className={`${String(styles.stack)} ${String(styles.quick)} ${String(styles.session)}`} ref={boxRef} aria-busy={gate.waiting}>
        {gate.waiting ? <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span> : null}
        {chips(readout.signals)}
        {gate.waiting ? (
          <span className={`${String(styles.reserve)} ${String(styles.reserveLinesQuick)} ${gate.skeleton ? String(skel.bar) : ''}`} />
        ) : detailFailed || detail === null ? null : (
          // Three, and no „+N" line under them: the card holds one height (owner, 25 September 2026).
          <div className={`${String(styles.linesQuick)} ${gate.reveal}`}>
            <ExerciseLines lines={detail.deviations.slice(0, 3)} />
          </div>
        )}
        {said()}
      </div>
    );
  }

  const ring = RING[density];
  return (
    <div className={`${String(styles.stack)} ${String(styles[density])} ${String(styles.session)}`} ref={boxRef} aria-busy={gate.waiting}>
      {gate.waiting ? <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span> : null}
      <div className={styles.key}>
        {readout.ring === null ? null : (
          <span className={styles.ring}>
            {/* The ring's hole is the sets done and the caption under it what they were out of. */}
            <ReadoutRing
              percent={readout.ring.percent}
              diameter={ring.diameter}
              thickness={ring.thickness}
              colour={RING_COLOUR[readout.ring.ink]}
              figure={readout.figure}
              label={readout.ringLabel}
              reducedMotion={reducedMotion}
              scale={ring.scale}
            />
            <span className={styles.ringCaption}>{readout.caption}</span>
          </span>
        )}
        {density === 'desk' ? (
          // The desk's ring stands 88 tall and the chips take one line of that: the client's words
          // go in the width beside the ring rather than in a row of their own under it (owner,
          // 25 September 2026 — a card is read whole without scrolling).
          <div className={styles.keySide}>
            {chips(readout.allSignals)}
            {gate.waiting || detail?.reflection == null ? null : (
              <blockquote className={`${String(styles.quote)} ${gate.reveal}`}>
                <span className={styles.quoteLabel}>{t('web:tasks.blocks.session.reflection')}</span>
                {detail.reflection}
              </blockquote>
            )}
          </div>
        ) : (
          <div className={styles.keySide}>
            {glance === '' ? null : <p className={styles.glanceLine}>{glance}</p>}
            <div className={styles.chipBand}>{chips(readout.allSignals)}</div>
            {said()}
          </div>
        )}
      </div>

      {density === 'desk' ? (
        <div className={styles.stack}>
          {gate.waiting ? (
            <span className={`${String(styles.reserve)} ${String(styles.reserveSession)} ${gate.skeleton ? String(skel.bar) : ''}`} />
          ) : detailFailed ? (
            <p className={`${String(styles.note)} ${gate.reveal}`}>{t('web:tasks.blocks.readFailed')}</p>
          ) : detail === null ? null : (
            <div className={`${String(styles.stack)} ${gate.reveal}`}>
              {/* Three: what stands above the open note at 1512×862; the rest is the „+N" line. */}
              <ExerciseLines lines={detail.deviations} unchanged={detail.unchanged} limit={3} />
            </div>
          )}
        </div>
      ) : null}

      {density === 'desk' && !gate.waiting ? <div className={styles.noteSlot}>{note}</div> : null}
    </div>
  );

  /**
   * The client's words on the two small cards. They hold their line from the first frame, so the
   * card does not grow when `/display` lands; the slot always says something once it has — the
   * words, that there were none, or that the read failed.
   */
  function said() {
    if (session.workoutExecutionId === null) return null;
    return (
      <p className={`${String(styles.said)} ${gate.waiting ? String(styles.saidWait) : gate.reveal}`}>
        {gate.waiting ? (
          gate.skeleton ? <span className={`${String(skel.bar)} ${String(styles.saidBar)}`} /> : null
        ) : detailFailed ? (
          t('web:tasks.blocks.readFailed')
        ) : detail === null ? null : detail.reflection === null ? (
          <span className={styles.saidNone}>{t('web:tasks.blocks.session.noReflection')}</span>
        ) : (
          <q>{detail.reflection}</q>
        )}
      </p>
    );
  }

  function chips(signals: typeof readout.signals) {
    return (
      <FeedbackSignalChips
        pending={chipsPending}
        signals={signals}
        sleep={sleep === null ? null : t('web:tasks.blocks.session.sleep', { value: decimal(sleep) })}
      />
    );
  }
}
